/**
 * "Levels" modal: shows every built-in level as a card grid plus the
 * user's own levels at the bottom. Clicking a card jumps to that level.
 */

import * as LevelStore from '../../../editor/LevelStore.js';
import * as Progress from '../../../gameplay/Progress.js';
import { formatFrameTime } from '../Theme.js';
import type { LevelData } from '../../../gameplay/LevelLoader.js';
import { Sfx } from '../../../audio/Sfx.js';
import { createModalShell, styleButton } from './modalUtils.js';

export interface LevelSelectController { close(): void }

export interface LevelSelectOptions {
  /** Built-in levels in display order. */
  allLevels: ReadonlyArray<LevelData>;
  /** Currently active built-in index, or -1 when in test mode / user level. */
  currentBuiltinIndex: number;
  /** Title to show in the header eyebrow ("currently on X"). */
  currentLevelName: string;
  /** Picked a built-in level (the host should call loadLevelByIndex). */
  onPickBuiltin: (index: number) => void;
  /** Picked a user level (the host should clear testMode + call loadLevelByData). */
  onPickUser: (entry: LevelStore.StoredLevel) => void;
  /** Asked to watch the saved best replay for a built-in level. The host
   *  should load the level with the replay queued for playback. */
  onWatchBest?: (index: number, encodedReplay: string) => void;
  onClose?: () => void;
}

export function openLevelSelectModal(opts: LevelSelectOptions): LevelSelectController {
  const shell = createModalShell({
    width: 'min(720px, 96vw)',
    backdrop: 'radial-gradient(circle at 50% 30%, rgba(80,40,140,0.35), rgba(0,0,0,0.75))',
    panelBackground: 'linear-gradient(180deg, #1a0e30 0%, #0a0518 100%)',
    panelBorder: '1px solid #6a4aaa',
    ...(opts.onClose ? { onClose: opts.onClose } : {}),
  });
  Object.assign(shell.panel.style, {
    padding: '20px', gap: '14px', borderRadius: '14px',
    boxShadow: '0 8px 40px rgba(120,80,255,0.25)',
  } as Partial<CSSStyleDeclaration>);
  // Backdrop blur (kept from original).
  Object.assign(shell.overlay.style, {
    backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
  } as Partial<CSSStyleDeclaration>);

  const header = document.createElement('div');
  Object.assign(header.style, { display: 'flex', alignItems: 'baseline', gap: '12px' } as Partial<CSSStyleDeclaration>);
  const heading = document.createElement('div');
  heading.textContent = 'LEVELS';
  Object.assign(heading.style, { fontWeight: '800', fontSize: '20px', letterSpacing: '0.12em' } as Partial<CSSStyleDeclaration>);
  header.appendChild(heading);
  const meta = document.createElement('div');
  meta.textContent = `${opts.allLevels.length} stages \u00b7 currently on ${opts.currentLevelName}`;
  Object.assign(meta.style, { fontSize: '12px', opacity: '0.65' } as Partial<CSSStyleDeclaration>);
  header.appendChild(meta);
  shell.panel.appendChild(header);

  const grid = document.createElement('div');
  Object.assign(grid.style, {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '8px', overflowY: 'auto', flex: '1', paddingRight: '4px',
  } as Partial<CSSStyleDeclaration>);

  const makeCard = (
    label: string, name: string, current: boolean, onClick: () => void,
    record: import('../../../gameplay/Progress.js').LevelRecord | null,
    onWatch: (() => void) | null,
  ): HTMLButtonElement => {
    const completed = record !== null;
    const btn = document.createElement('button');
    Object.assign(btn.style, {
      textAlign: 'left', padding: '10px 12px',
      background: current
        ? 'linear-gradient(180deg, #4a2a8e, #2a1a4e)'
        : (completed ? 'linear-gradient(180deg, #1f2a18, #15211a)' : '#1a1130'),
      color: '#eee',
      border: current
        ? '1px solid #cc88ff'
        : (completed ? '1px solid #6ad08a' : '1px solid #3a2a5a'),
      borderRadius: '8px', cursor: 'pointer', font: 'inherit',
      display: 'flex', flexDirection: 'column', gap: '4px',
      transition: 'background 0.15s, border-color 0.15s',
      position: 'relative',
    } as Partial<CSSStyleDeclaration>);
    btn.addEventListener('mouseenter', () => {
      if (current) return;
      btn.style.background = completed ? 'linear-gradient(180deg, #2a3a23, #1d2c20)' : '#251945';
    });
    btn.addEventListener('mouseleave', () => {
      if (current) return;
      btn.style.background = completed ? 'linear-gradient(180deg, #1f2a18, #15211a)' : '#1a1130';
    });
    const top = document.createElement('div');
    Object.assign(top.style, {
      fontSize: '11px', opacity: '0.7', letterSpacing: '0.08em',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px',
    } as Partial<CSSStyleDeclaration>);
    const labelSpan = document.createElement('span');
    labelSpan.textContent = label;
    top.appendChild(labelSpan);
    if (completed) {
      // Gold star + best-time badge. Sized & positioned so it never wraps
      // onto a second line of the card title.
      const badge = document.createElement('span');
      badge.textContent = `\u2605 ${formatFrameTime(record!.bestFrames)}`;
      Object.assign(badge.style, {
        color: '#ffd76a', fontWeight: '700', fontSize: '11px',
        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
        letterSpacing: '0.04em',
      } as Partial<CSSStyleDeclaration>);
      top.appendChild(badge);
    }
    const main = document.createElement('div');
    Object.assign(main.style, { fontSize: '15px', fontWeight: '600' } as Partial<CSSStyleDeclaration>);
    main.textContent = name;
    btn.appendChild(top);
    btn.appendChild(main);
    if (completed && onWatch) {
      // Tiny "watch best replay" link rendered as text so the click area
      // doesn't fight the parent card. Click bubbles are stopped so
      // the card's main click doesn't fire.
      const watch = document.createElement('div');
      watch.textContent = '\u25B6 Watch best replay';
      Object.assign(watch.style, {
        fontSize: '11px', color: '#9ed8a6', marginTop: '2px',
        cursor: 'pointer', alignSelf: 'flex-start',
        padding: '2px 6px', borderRadius: '4px',
        background: 'rgba(106,208,138,0.10)',
        border: '1px solid rgba(106,208,138,0.35)',
      } as Partial<CSSStyleDeclaration>);
      watch.addEventListener('mouseenter', () => { watch.style.background = 'rgba(106,208,138,0.22)'; });
      watch.addEventListener('mouseleave', () => { watch.style.background = 'rgba(106,208,138,0.10)'; });
      watch.addEventListener('click', (e) => {
        e.stopPropagation();
        Sfx.uiClick();
        shell.close();
        onWatch();
      });
      btn.appendChild(watch);
    }
    btn.addEventListener('click', () => { Sfx.uiClick(); shell.close(); onClick(); });
    return btn;
  };

  let completedCount = 0;
  for (let i = 0; i < opts.allLevels.length; i++) {
    const lvl = opts.allLevels[i]!;
    const current = i === opts.currentBuiltinIndex;
    const num = String(i + 1).padStart(2, '0');
    const record = Progress.getRecord(lvl.id);
    if (record) completedCount++;
    const onWatch = (record && opts.onWatchBest)
      ? () => opts.onWatchBest!(i, record.replay)
      : null;
    grid.appendChild(makeCard(
      `STAGE ${num}`, lvl.name, current,
      () => opts.onPickBuiltin(i),
      record, onWatch,
    ));
  }
  // Update the meta line with completion progress so the user sees their
  // overall campaign status at a glance.
  meta.textContent = `${completedCount}/${opts.allLevels.length} complete \u00b7 currently on ${opts.currentLevelName}`;

  const userLevels = LevelStore.listUser();
  if (userLevels.length > 0) {
    const sep = document.createElement('div');
    Object.assign(sep.style, {
      gridColumn: '1 / -1',
      fontSize: '11px', opacity: '0.6', letterSpacing: '0.12em',
      marginTop: '8px', borderTop: '1px solid #3a2a5a', paddingTop: '8px',
    } as Partial<CSSStyleDeclaration>);
    sep.textContent = 'MY LEVELS';
    grid.appendChild(sep);
    for (const s of userLevels) {
      grid.appendChild(makeCard(s.meta.slug.toUpperCase(), s.data.name, false, () => opts.onPickUser(s), null, null));
    }
  }
  shell.panel.appendChild(grid);

  const footer = document.createElement('div');
  Object.assign(footer.style, { display: 'flex', justifyContent: 'space-between', alignItems: 'center' } as Partial<CSSStyleDeclaration>);
  const hint = document.createElement('div');
  hint.textContent = 'Tap a stage to jump in';
  Object.assign(hint.style, { fontSize: '12px', opacity: '0.6' } as Partial<CSSStyleDeclaration>);
  footer.appendChild(hint);
  const closeBtn = document.createElement('button');
  closeBtn.textContent = 'Close';
  styleButton(closeBtn);
  Object.assign(closeBtn.style, {
    padding: '8px 18px', border: '1px solid #6a4aaa', borderRadius: '6px',
  } as Partial<CSSStyleDeclaration>);
  closeBtn.addEventListener('click', shell.close);
  footer.appendChild(closeBtn);
  shell.panel.appendChild(footer);

  document.body.appendChild(shell.overlay);
  return { close: shell.close };
}
